import { useCallback, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { shiftApi } from "../../../api/resources";
import type { ShiftAdmin, ShiftQuery } from "../../../api/types";
import { Button, cn } from "../../../components/ui";
import { LoadState, useLoad } from "../shared";
import { AttentionIcon, CrewBadges, SHIFT_TONE, addDays, isoDate } from "./shiftUi";
import { DayView } from "./DayView";

type Mode = "day" | "week" | "month";
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function gridRange(anchor: Date, mode: Mode): Date[] {
  if (mode === "day") return [new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate())];
  const start = mode === "month" ? new Date(anchor.getFullYear(), anchor.getMonth(), 1) : new Date(anchor);
  start.setDate(start.getDate() - start.getDay());
  const days = mode === "month" ? 42 : 7;
  return Array.from({ length: days }, (_, i) => addDays(start, i));
}

function Chip({ s, onOpen, detailed }: { s: ShiftAdmin; onOpen: (id: string) => void; detailed: boolean }) {
  return (
    <button type="button" onClick={() => onOpen(s.id)} data-testid={`calendar-shift-${s.id}`}
      className={cn("flex w-full items-start gap-1 rounded-sm border px-1.5 py-1 text-left text-[11px] leading-tight transition-colors hover:brightness-125", SHIFT_TONE[s.status])}>
      <AttentionIcon show={s.needs_attention} testId={`calendar-shift-flag-${s.id}`} />
      <span className="min-w-0">
        <span className="block truncate font-semibold">{s.start_label ? s.start_label : "No times"} · {s.employee_name}</span>
        {detailed && <span className="block truncate opacity-80">{s.venue_name}{s.role ? ` · ${s.role}` : ""}</span>}
        <CrewBadges s={s} testPrefix="calendar-week" compact />
      </span>
    </button>
  );
}

export function ShiftCalendar({ filters, reload, onOpen }: { filters: ShiftQuery; reload: number; onOpen: (id: string) => void }) {
  const [mode, setMode] = useState<Mode>("month");
  const [anchor, setAnchor] = useState(() => new Date());
  const days = useMemo(() => gridRange(anchor, mode), [anchor, mode]);
  const from = isoDate(days[0]);
  const to = isoDate(days[days.length - 1]);
  const key = JSON.stringify({ ...filters, scope: undefined, date_from: from, date_to: to });
  const loader = useCallback(() => shiftApi.list(JSON.parse(key) as ShiftQuery), [key, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<ShiftAdmin[]>(loader);
  const byDay = useMemo(() => {
    const m: Record<string, ShiftAdmin[]> = {};
    (data ?? []).forEach((s) => (m[s.work_date] ??= []).push(s));
    return m;
  }, [data]);
  const move = (dir: number) => setAnchor((a) => (mode === "month" ? new Date(a.getFullYear(), a.getMonth() + dir, 1) : addDays(a, (mode === "week" ? 7 : 1) * dir)));
  const openDay = (d: Date) => { setAnchor(d); setMode("day"); };
  const today = isoDate(new Date());
  const title = mode === "day"
    ? anchor.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" })
    : mode === "month"
    ? anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : `${days[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <div data-testid="shift-calendar">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" className="h-9 px-3" onClick={() => move(-1)} aria-label="Previous" data-testid="calendar-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="secondary" className="h-9 px-3 text-[11px]" onClick={() => setAnchor(new Date())} data-testid="calendar-today-button">Today</Button>
          <Button variant="secondary" className="h-9 px-3" onClick={() => move(1)} aria-label="Next" data-testid="calendar-next-button"><ChevronRight className="h-4 w-4" /></Button>
          <h2 data-testid="calendar-title" className="w-full font-display text-xl font-bold sm:ml-3 sm:w-auto sm:text-2xl uppercase tracking-tight text-white">{title}</h2>
        </div>
        <div className="flex rounded-sm border border-white/15 p-0.5">
          {(["day", "week", "month"] as Mode[]).map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)} data-testid={`calendar-mode-${m}`}
              className={cn("px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em]", mode === m ? "rounded-[10px] bg-[rgba(255,29,0,0.16)] text-white shadow-[0_0_14px_-4px_rgba(255,29,0,0.7)]" : "text-zinc-400 hover:text-white")}>
              {m}
            </button>
          ))}
        </div>
      </div>
      <LoadState error={error} loading={false} testId="calendar-error" />
      {mode === "day" ? <DayView shifts={byDay[from] ?? []} onOpen={onOpen} /> : (
      <div className="grid grid-cols-7 overflow-hidden rounded-sm border border-white/10 bg-white/10 [gap:1px]">
        {WEEKDAYS.map((d) => <div key={d} className="label-caps bg-surface px-2 py-2 text-zinc-500">{d}</div>)}
        {days.map((d) => {
          const iso = isoDate(d);
          const items = byDay[iso] ?? [];
          const outside = mode === "month" && d.getMonth() !== anchor.getMonth();
          return (
            <div key={iso} data-testid={`calendar-day-${iso}`} className={cn("space-y-1 bg-ink p-1.5", mode === "week" ? "min-h-[420px]" : "min-h-[112px]", outside && "opacity-40")}>
              <p className="mb-1 text-right"><button type="button" onClick={() => openDay(d)} aria-label={`Open ${d.toDateString()} in day view`} data-testid={`calendar-day-open-${iso}`}
                className={cn("rounded px-1 font-mono text-xs hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/70", iso === today ? "font-bold text-signal" : "text-zinc-500")}>{d.getDate()}</button></p>
              {items.slice(0, mode === "month" ? 3 : 50).map((s) => <Chip key={s.id} s={s} onOpen={onOpen} detailed={mode === "week"} />)}
              {mode === "month" && items.length > 3 && (
                <button type="button" onClick={() => openDay(d)} data-testid={`calendar-more-${iso}`} className="w-full text-left text-[11px] text-zinc-400 hover:text-white">
                  +{items.length - 3} more
                </button>
              )}
            </div>
          );
        })}
      </div>
      )}
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-zinc-500">
        <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-warn/60" /> Tentative</span>
        <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-emerald-500/60" /> Confirmed</span>
        <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-zinc-600" /> Cancelled</span>
        <span className="flex items-center gap-1.5"><AttentionIcon show testId="calendar-legend-flag" /> Needs attention</span>
        <span>Times shown in each venue's local time.</span>
      </div>
    </div>
  );
}
